import { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Database, Download, Upload, ShieldOff, HardDriveDownload, HardDriveUpload } from "lucide-react";

const FILE_LIMIT_BYTES = 8 * 1024 * 1024; // 8 MB safety cap on restore uploads

async function readJson(file) {
  return await new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => {
      try { res(JSON.parse(r.result)); } catch (e) { rej(e); }
    };
    r.onerror = rej;
    r.readAsText(file);
  });
}

export default function BackupRestore() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const role = user?.role;
  const canBackup = role === "managing_director" || role === "super_admin" || role === "hr_admin";
  const canRestore = role === "managing_director" || role === "super_admin";
  const fileRef = useRef();
  const [preview, setPreview] = useState(null);   // parsed JSON
  const [mode, setMode] = useState("replace");
  const [confirmText, setConfirmText] = useState("");
  const [downloading, setDownloading] = useState(false);

  const { data: summary = {} } = useQuery({
    queryKey: ["data-summary"],
    queryFn: async () => (await api.get("/admin/data-summary")).data,
    enabled: canBackup,
  });

  const restoreMut = useMutation({
    mutationFn: (body) => api.post("/admin/restore", body),
    onSuccess: (res) => {
      toast.success(`Restored ${res.data.total} rows across ${Object.keys(res.data.restored).length} collections (${res.data.mode} mode)`);
      setPreview(null); setConfirmText("");
      qc.invalidateQueries();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Restore failed"),
  });

  const downloadBackup = async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${API_BASE}/admin/backup`, { credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      const disp = res.headers.get("content-disposition") || "";
      const nameMatch = disp.match(/filename=([^;]+)/i);
      const filename = nameMatch ? nameMatch[1].trim() : `dreamhome-erp-backup-${Date.now()}.json`;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Backup downloaded (${(blob.size / 1024).toFixed(1)} KB)`);
    } catch (e) {
      toast.error("Backup failed. Check that you're signed in as MD/HR.");
    } finally {
      setDownloading(false);
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > FILE_LIMIT_BYTES) {
      toast.error("Backup file too large. Max 8 MB.");
      return;
    }
    try {
      const parsed = await readJson(file);
      if (!parsed?.version || !parsed?.data) {
        toast.error("Invalid backup file. Missing version/data.");
        return;
      }
      setPreview(parsed);
    } catch {
      toast.error("Could not parse backup JSON.");
    }
  };

  if (!canBackup) {
    return (
      <div className="p-8 max-w-2xl" data-testid="backup-restore-page">
        <div className="border border-zinc-200 rounded-lg bg-white p-8 flex items-start gap-4">
          <div className="w-10 h-10 rounded-md border border-red-200 bg-red-50 grid place-items-center shrink-0">
            <ShieldOff className="w-5 h-5 text-red-700" />
          </div>
          <div>
            <h1 className="font-heading text-xl font-extrabold tracking-tight">Access denied</h1>
            <p className="text-sm text-zinc-600 mt-2">Only the Managing Director or HR Admin can access Backup &amp; Restore.</p>
          </div>
        </div>
      </div>
    );
  }

  const previewRows = preview ? Object.entries(preview.counts || {}).filter(([k]) => k !== "total") : [];
  const readyToRestore = preview && confirmText.trim() === "RESTORE";

  return (
    <div className="p-8 max-w-5xl space-y-8" data-testid="backup-restore-page">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-md border border-zinc-200 grid place-items-center bg-white">
          <Database className="w-4 h-4 text-zinc-700" />
        </div>
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight">Backup &amp; Restore</h1>
          <p className="text-sm text-zinc-500 mt-0.5">Take a JSON snapshot of your organization&apos;s business data, or restore from one.</p>
        </div>
      </div>

      {/* Backup */}
      <section className="border border-zinc-200 rounded-lg bg-white">
        <div className="px-5 py-3 border-b border-zinc-100 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold flex items-center gap-2">
          <HardDriveDownload className="w-3.5 h-3.5" /> Backup
        </div>
        <div className="px-5 py-5 flex items-start justify-between gap-6">
          <div className="text-sm text-zinc-700 space-y-1">
            <div>Snapshot includes all business collections filtered to your organization.</div>
            <div className="text-zinc-500 text-xs">
              Current data: <span className="font-mono text-zinc-800" data-testid="backup-current-total">{summary.total ?? 0}</span> rows.
              Store the file securely — it contains client, project and financial data.
            </div>
          </div>
          <Button onClick={downloadBackup} disabled={downloading} className="gap-2 shrink-0" data-testid="backup-download">
            <Download className="w-4 h-4" />
            {downloading ? "Preparing…" : "Download backup"}
          </Button>
        </div>
      </section>

      {/* Restore */}
      {canRestore ? (
      <section className="border border-zinc-200 rounded-lg bg-white">
        <div className="px-5 py-3 border-b border-zinc-100 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold flex items-center gap-2">
          <HardDriveUpload className="w-3.5 h-3.5" /> Restore from backup
        </div>
        <div className="px-5 py-5 space-y-5">
          <div className="flex items-center gap-3">
            <input ref={fileRef} type="file" accept="application/json,.json" onChange={onFile} className="hidden" data-testid="restore-file-input" />
            <Button variant="outline" onClick={() => fileRef.current?.click()} className="gap-2" data-testid="restore-choose-file">
              <Upload className="w-4 h-4" /> Choose backup file
            </Button>
            {preview && <span className="text-xs text-zinc-500 font-mono">v{preview.version} · {preview.generated_at?.slice(0, 19).replace("T", " ")}</span>}
          </div>

          {preview && (
            <>
              <div className="border border-zinc-200 rounded-md">
                <div className="px-4 py-2 bg-zinc-50 border-b border-zinc-100 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Preview</div>
                <div className="grid grid-cols-3 gap-x-6 gap-y-1 px-4 py-3">
                  {previewRows.map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between text-xs py-0.5 border-b border-zinc-100" data-testid={`preview-${k}`}>
                      <span className="text-zinc-600">{k.replace(/_/g, " ")}</span>
                      <span className="font-mono text-zinc-900">{v}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Label>Mode</Label>
                <div className="flex items-center gap-4 mt-2 text-sm">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" checked={mode === "replace"} onChange={() => setMode("replace")} data-testid="mode-replace" />
                    <span><b>Replace</b> — wipe target collections before insert (recommended)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" checked={mode === "merge"} onChange={() => setMode("merge")} data-testid="mode-merge" />
                    <span><b>Merge</b> — insert alongside existing rows</span>
                  </label>
                </div>
              </div>

              <div>
                <Label htmlFor="restore-confirm">Type <span className="font-mono font-bold text-amber-700">RESTORE</span> to enable</Label>
                <Input
                  id="restore-confirm"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                  className="mt-1 h-9 font-mono w-40"
                  placeholder="RESTORE"
                  data-testid="restore-confirm-input"
                />
              </div>

              <Button
                onClick={() => restoreMut.mutate({ confirm: "RESTORE", payload: preview, mode })}
                disabled={!readyToRestore || restoreMut.isPending}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="restore-confirm-btn"
              >
                {restoreMut.isPending ? "Restoring…" : `Restore (${mode})`}
              </Button>
            </>
          )}
        </div>
      </section>
      ) : (
        <div className="border border-zinc-200 rounded-lg bg-zinc-50 p-5 text-xs text-zinc-600 flex items-start gap-2" data-testid="restore-hr-notice">
          <ShieldOff className="w-3.5 h-3.5 mt-0.5 text-zinc-500" />
          <div>Restore is limited to the Managing Director. HR can download backups but cannot restore them.</div>
        </div>
      )}
    </div>
  );
}
